import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Check, CheckCheck, AlertCircle, Reply, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Citacao } from '@/lib/citacao';
import { Database } from '@/integrations/supabase/types';
import { ChatMediaContent } from './ChatMediaContent';
import { CitacaoBloco } from './CitacaoBloco';
import { ReactionBar } from './ReactionBar';

type RemetenteMensagem = Database['public']['Enums']['remetente_tipo'];

interface ChatMessageProps {
  conteudo: string;
  remetente: RemetenteMensagem;
  nomeRemetente?: string;
  avatarUrl?: string | null;
  dataHora: string;
  isOwn?: boolean;
  canal?: 'interno' | 'whatsapp' | 'app';
  statusEntrega?: 'sent' | 'delivered' | 'read' | 'failed' | null;
  lidaConsumer?: boolean;
  mensagemId: string;
  tipoMedia?: 'image' | 'audio' | 'video' | 'document' | null;
  mediaStatus?: 'pending' | 'ready' | 'failed' | null;
  mediaStoragePath?: string | null;
  mediaMimeType?: string | null;
  mediaFilename?: string | null;
  bspMessageId?: string | null;
  quoted?: Citacao | null;
  onReply?: () => void;
  reacao?: string | null;
  onReact?: (emoji: string) => void;
}

function deliveryIconFor(status: ChatMessageProps['statusEntrega']) {
  switch (status) {
    case 'sent':
      return <Check size={11} aria-label="enviada" />;
    case 'delivered':
      return <CheckCheck size={11} aria-label="entregue" />;
    case 'read':
      return (
        <CheckCheck
          size={11}
          style={{ color: 'hsl(var(--chat-read-tick))' }}
          aria-label="lida"
        />
      );
    case 'failed':
      return <AlertCircle size={11} className="text-destructive" aria-label="falha" />;
    default:
      // Pendente (status null): mensagem própria de WhatsApp ainda por confirmar
      // (bolha otimista ou janela curta antes do 'sent'). Mostra "a enviar".
      return <Clock size={11} aria-label="a enviar" />;
  }
}

export function ChatMessage({
  conteudo,
  remetente,
  nomeRemetente,
  avatarUrl,
  dataHora,
  isOwn = false,
  canal,
  statusEntrega,
  lidaConsumer,
  mensagemId,
  tipoMedia,
  mediaStatus,
  mediaStoragePath,
  mediaMimeType,
  mediaFilename,
  bspMessageId,
  quoted,
  onReply,
  reacao = null,
  onReact,
}: ChatMessageProps) {
  const { t } = useTranslation();
  const hora = format(new Date(dataHora), 'HH:mm', { locale: ptBR });
  const inicial = nomeRemetente?.charAt(0)?.toUpperCase() || (isOwn ? 'E' : 'C');

  const hasMedia = !!tipoMedia && !!mediaStatus;
  const mediaNode = hasMedia ? (
    <ChatMediaContent
      mensagemId={mensagemId}
      tipoMedia={tipoMedia!}
      mediaStatus={mediaStatus!}
      mediaStoragePath={mediaStoragePath ?? null}
      mediaMimeType={mediaMimeType ?? null}
      mediaFilename={mediaFilename ?? null}
      canal={canal}
    />
  ) : null;

  const showDeliveryStatus = canal === 'whatsapp' && (isOwn || remetente === 'atendente');
  const deliveryIcon = showDeliveryStatus ? deliveryIconFor(statusEntrega) : null;

  // Tique do canal app (próprio): cinza 'enviada' → cor 'lida' (lido pelo cliente).
  const appTickOwn = canal === 'app' && (isOwn || remetente === 'atendente');
  const appTick = appTickOwn
    ? (lidaConsumer
        ? <CheckCheck size={11} style={{ color: 'hsl(var(--chat-read-tick))' }} aria-label="lida" />
        : <CheckCheck size={11} aria-label="enviada" />)
    : null;

  if (remetente === 'sistema') {
    return <div className="msg--sys">{conteudo}</div>;
  }

  const own = isOwn || remetente === 'atendente';

  return (
    <div className={`msg ${own ? 'msg--out' : 'msg--in'}`}>
      {!own && (
        avatarUrl ? (
          <img src={avatarUrl} alt="" className="msg__avatar object-cover" />
        ) : (
          <span className="msg__avatar" aria-hidden>
            {inicial}
          </span>
        )
      )}
      <div className="msg__col">
        <div className={`msg__bubble${reacao ? ' msg__bubble--reagida' : ''}`}>
          {quoted && (
            <div className="msg__quoted">
              <CitacaoBloco citacao={quoted} />
            </div>
          )}
          {mediaNode}
          {conteudo && <span>{conteudo}</span>}
          {reacao && <span className="msg__reacao" aria-label="reação">{reacao}</span>}
        </div>
        <span className="msg__meta">
          {hora}
          {deliveryIcon}
          {appTick}
        </span>
      </div>
      {canal === 'whatsapp' && (
        // Renderizado sempre (para WhatsApp) a reservar o espaço; fica escondido
        // e inativo enquanto não há bsp_message_id (bolha otimista "a enviar"),
        // evitando o salto da bolha quando a linha real chega com o botão.
        <button
          type="button"
          className="msg__reply-btn"
          aria-label={t('panel.citacao.replyAria')}
          onClick={onReply}
          disabled={!bspMessageId || !onReply}
        >
          <Reply size={13} />
        </button>
      )}
      {canal === 'whatsapp' && remetente === 'cliente' && bspMessageId && onReact && (
        <ReactionBar onReact={onReact} />
      )}
    </div>
  );
}
